import { useEffect, useState } from "react";
import { getSiteSettings } from "@/lib/servicesApi";
import { SALES } from "@/components/report/salesConfig";

// Fallbacks used until (or if) GET /api/site-settings answers. They equal the
// server defaults so the page does not jump. free_slots_left falls back to 0 on
// purpose: an unknown number of free slots must never be advertised.
export const DEFAULT_SETTINGS = {
  report_price_eur: 39,
  consulting_price_eur: 149,
  consulting_days: 30,
  max_tickets: 50,
  response_hours: 24,
  free_slots_total: 100,
  free_slots_left: 0,
};

const num = (v, fallback, min = 0) =>
  typeof v === "number" && Number.isFinite(v) && v >= min ? v : fallback;

// Tolerant of missing/garbled fields; legal_live falls back to the constant
// (read at call time so tests can flip it).
export function normalizeSettings(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const d = DEFAULT_SETTINGS;
  const total = num(r.free_slots_total, d.free_slots_total);
  return {
    report_price_eur: num(r.report_price_eur, d.report_price_eur),
    consulting_price_eur: num(r.consulting_price_eur, d.consulting_price_eur),
    consulting_days: num(r.consulting_days, d.consulting_days, 1),
    max_tickets: num(r.max_tickets, d.max_tickets, 1),
    response_hours: num(r.response_hours, d.response_hours, 1),
    free_slots_total: total,
    free_slots_left: Math.min(num(r.free_slots_left, d.free_slots_left), total),
    legal_live: typeof r.legal_live === "boolean" ? r.legal_live : !!SALES.legalPagesLive,
    // Strictly opt-in: anything but a real `true` keeps the request flow.
    payments_live: r.payments_live === true,
  };
}

// "Noch {n} von {total} Plätzen" line: null when no slot is left, so the caller
// simply renders nothing.
export function freeSlotsLine(template, settings) {
  const s = settings || normalizeSettings(null);
  if (!(s.free_slots_left > 0) || typeof template !== "string") return null;
  return template.replace(/\{n\}/g, String(s.free_slots_left)).replace(/\{total\}/g, String(s.free_slots_total));
}

// "Noch 100 von 100 Plätzen" reads as "nobody has bought this yet" for as
// long as free_slots_left still equals free_slots_total — the countdown
// template is only picked once that's no longer true (owner-relayed review,
// 2026-09-28). `templates` is `{ some, all }`; `all` has no `{n}` to fill.
export function slotsTemplate(templates, settings) {
  const s = settings || normalizeSettings(null);
  return s.free_slots_total > 0 && s.free_slots_left === s.free_slots_total ? templates.all : templates.some;
}

// Replaces {key} placeholders; unknown keys stay untouched.
export const fillTemplate = (tpl, vars) =>
  String(tpl).replace(/\{(\w+)\}/g, (m, k) => (vars && vars[k] !== undefined ? String(vars[k]) : m));

export const eur =(n, lang = "de") => (lang === "en" ? `€${n}` : `${n} €`);

let cache = null;
let inflight = null;
export function resetSiteSettingsCache() {
  cache = null;
  inflight = null;
}

// One request per page load, shared by every consumer. Returns fallbacks first.
export function useSiteSettings() {
  const [raw, setRaw] = useState(cache);
  useEffect(() => {
    if (cache) return undefined;
    let alive = true;
    inflight = inflight || getSiteSettings().then((d) => (cache = d)).catch(() => null);
    inflight.then((d) => {
      if (alive && d) setRaw(d);
    });
    return () => {
      alive = false;
    };
  }, []);
  return normalizeSettings(raw);
}
